import React from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  Linkedin,
  Loader2,
  Lock,
  RefreshCw,
  Sparkles,
  TrendingUp,
  UploadCloud,
  Wand2,
  X,
  XCircle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/lib/checkout";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SUPPORT_EMAIL = "getcvaipro@gmail.com";

const PROCESSING_STEPS = [
  "Reading your CV…",
  "Scoring ATS compatibility…",
  "Finding weaknesses…",
  "Rewriting with recruiter-grade language…",
  "Polishing layout & keywords…",
];

export default function UploadResume() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { startCheckout, loadingPlan } = useCheckout();

  const [phase, setPhase] = React.useState("checking"); // checking | no-credit | form | processing | result
  const [orders, setOrders] = React.useState(null);
  const [job, setJob] = React.useState(null);
  const [stepIdx, setStepIdx] = React.useState(0);
  const [feedback, setFeedback] = React.useState("");
  const [downloading, setDownloading] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [variantPosting, setVariantPosting] = React.useState("");
  const [variantBusy, setVariantBusy] = React.useState(false);

  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [linkedin, setLinkedin] = React.useState("");
  const [role, setRole] = React.useState("");
  const [experience, setExperience] = React.useState("");
  const [skills, setSkills] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [jobPosting, setJobPosting] = React.useState("");
  const [language, setLanguage] = React.useState("en");
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const fileInputRef = React.useRef(null);

  // --- auth gate ---
  React.useEffect(() => {
    if (user === false) navigate("/login?next=/upload", { replace: true });
  }, [user, navigate]);

  React.useEffect(() => {
    if (user && user !== false) {
      setFullName((v) => v || user.name || "");
      setEmail((v) => v || user.email || "");
    }
  }, [user]);

  // --- poll a job until done ---
  const pollJob = React.useCallback(async (jobId) => {
    for (let i = 0; i < 60; i += 1) {
      try {
        const { data } = await axios.get(`${API}/cv/jobs/${jobId}`, { withCredentials: true });
        if (data.status === "completed" || data.status === "failed") {
          setJob(data);
          setPhase(data.status === "completed" ? "result" : "form");
          if (data.status === "failed") {
            toast.error("Generation failed", {
              description: data.error || "Please try again — your credit has not been lost.",
            });
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      } catch (_) {}
      await new Promise((r) => setTimeout(r, 3000));
    }
    toast.error("Taking longer than expected", {
      description: "Check your account page in a few minutes — your resume will be there.",
    });
    setPhase("form");
  }, []);

  // --- initial load: existing job (?job=) or entitlement check ---
  React.useEffect(() => {
    if (!user || user === false) return;
    const jobId = params.get("job");
    (async () => {
      if (jobId) {
        try {
          const { data } = await axios.get(`${API}/cv/jobs/${jobId}`, { withCredentials: true });
          setJob(data);
          if (data.status === "processing") {
            setPhase("processing");
            pollJob(jobId);
          } else if (data.status === "completed") {
            setPhase("result");
          } else {
            setPhase("form");
          }
          return;
        } catch (_) {
          toast.error("Couldn't load that resume", { description: "It may not belong to this account." });
        }
      }
      try {
        const { data } = await axios.get(`${API}/payments/orders`, { withCredentials: true });
        setOrders(data);
        setPhase(data.available_count > 0 ? "form" : "no-credit");
      } catch (_) {
        setPhase("no-credit");
      }
    })();
  }, [user, params, pollJob]);

  // --- staged processing animation ---
  React.useEffect(() => {
    if (phase !== "processing") return;
    setStepIdx(0);
    const t = setInterval(() => setStepIdx((i) => Math.min(i + 1, PROCESSING_STEPS.length - 1)), 6000);
    return () => clearInterval(t);
  }, [phase]);

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large", { description: "Please upload a file under 10MB." });
      return;
    }
    if (f.type && !allowed.includes(f.type)) {
      toast.error("Unsupported file type", { description: "Please upload PDF, DOCX, or TXT." });
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error("Missing information", { description: "Full name and email are required." });
      return;
    }
    if (!file && !experience.trim()) {
      toast.error("Nothing to analyze", {
        description: "Upload your current CV or fill in your work experience.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("full_name", fullName.trim());
      form.append("email", email.trim());
      if (linkedin.trim()) form.append("linkedin", linkedin.trim());
      if (role.trim()) form.append("desired_role", role.trim());
      if (experience.trim()) form.append("work_experience", experience.trim());
      if (skills.trim()) form.append("skills", skills.trim());
      if (notes.trim()) form.append("notes", notes.trim());
      if (jobPosting.trim()) form.append("job_posting", jobPosting.trim());
      form.append("language", language);
      if (file) form.append("file", file);
      const { data } = await axios.post(`${API}/cv/generate`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      setPhase("processing");
      window.scrollTo({ top: 0, behavior: "smooth" });
      pollJob(data.id);
    } catch (err) {
      toast.error("Couldn't start generation", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const pollVariants = React.useCallback(async (jobId) => {
    for (let i = 0; i < 60; i += 1) {
      try {
        const { data } = await axios.get(`${API}/cv/jobs/${jobId}`, { withCredentials: true });
        setJob(data);
        const anyProcessing = (data.variants || []).some((v) => v.status === "processing");
        if (!anyProcessing) return;
      } catch (_) {}
      await new Promise((r) => setTimeout(r, 3000));
    }
  }, []);

  const handleAddVariant = async () => {
    if (!job || !variantPosting.trim()) return;
    setVariantBusy(true);
    try {
      await axios.post(
        `${API}/cv/jobs/${job.id}/variants`,
        { job_posting: variantPosting.trim() },
        { withCredentials: true }
      );
      setVariantPosting("");
      toast.success("AI is targeting the new job ad", { description: "Usually ready in under a minute." });
      pollVariants(job.id);
    } catch (err) {
      toast.error("Couldn't start the targeted version", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setVariantBusy(false);
    }
  };

  const handleVariantDownload = async (variantId, format) => {
    if (!job) return;
    const key = `${variantId}:${format}`;
    setDownloading(key);
    try {
      const res = await axios.get(`${API}/cv/jobs/${job.id}/variants/${variantId}/download?format=${format}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const baseName = (job.resume?.full_name || "resume").replace(/\s+/g, "_");
      const ext = format.endsWith("docx") ? "docx" : "pdf";
      const kind = format.startsWith("cover") ? "Cover_Letter" : "CV_targeted";
      a.href = url;
      a.download = `${baseName}_${kind}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Download failed", { description: "Please try again." });
    } finally {
      setDownloading(null);
    }
  };

  const handleRegenerate = async () => {    if (!job) return;
    try {
      await axios.post(
        `${API}/cv/jobs/${job.id}/regenerate`,
        { feedback: feedback.trim() || null },
        { withCredentials: true }
      );
      setFeedback("");
      setPhase("processing");
      window.scrollTo({ top: 0, behavior: "smooth" });
      pollJob(job.id);
    } catch (err) {
      toast.error("Couldn't regenerate", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    }
  };

  const handleDownload = async (format) => {
    if (!job) return;
    setDownloading(format);
    try {
      const res = await axios.get(`${API}/cv/jobs/${job.id}/download?format=${format}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const baseName = (job.resume?.full_name || "resume").replace(/\s+/g, "_");
      const fileNames = {
        pdf: `${baseName}_CV_AI_PRO.pdf`,
        docx: `${baseName}_CV_AI_PRO.docx`,
        cover_pdf: `${baseName}_Cover_Letter.pdf`,
        cover_docx: `${baseName}_Cover_Letter.docx`,
      };
      a.href = url;
      a.download = fileNames[format] || `${baseName}_${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Download failed", { description: "Please try again." });
    } finally {
      setDownloading(null);
    }
  };

  // ---------- renders ----------

  if (!user || user === false || phase === "checking") {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="upload-loading">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="upload-page">
      <div aria-hidden className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-600/15 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-purple-600/15 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg" />

      <header className="relative z-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="upload-logo-link">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/account"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="upload-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            My account
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-24">
        <div className="mb-6">
          <VerifyEmailBanner />
        </div>
        <AnimatePresence mode="wait">
          {/* ---------- NO CREDIT ---------- */}
          {phase === "no-credit" && (
            <motion.div
              key="no-credit"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-center pt-10"
              data-testid="upload-no-credit"
            >
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30">
                <Lock className="h-7 w-7 text-amber-400" />
              </div>
              <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance">
                One step away from your new CV.
              </h1>
              <p className="mt-4 max-w-xl mx-auto text-zinc-400 text-balance">
                The AI studio unlocks after purchase. Choose a plan and your credit
                will be waiting here instantly.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center">
                <button
                  onClick={() => startCheckout("cv_pro")}
                  disabled={loadingPlan === "cv_pro"}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                  data-testid="no-credit-buy-pro"
                >
                  {loadingPlan === "cv_pro" && <Loader2 className="h-4 w-4 animate-spin" />}
                  Get PRO — $26.01
                </button>
                <Link
                  to="/#pricing"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors"
                  data-testid="no-credit-see-plans"
                >
                  Compare all plans
                </Link>
              </div>
              {orders?.orders?.some((o) => o.payment_status === "pending") && (
                <p className="mt-8 text-xs text-zinc-500">
                  You have a payment still processing — it will appear here automatically once confirmed.
                </p>
              )}
            </motion.div>
          )}

          {/* ---------- PROCESSING ---------- */}
          {phase === "processing" && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center pt-16"
              data-testid="upload-processing"
            >
              <div className="relative inline-flex h-20 w-20 items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-blue-500/20 animate-ping" />
                <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500/40 to-purple-500/40 ring-1 ring-white/10">
                  <Wand2 className="h-7 w-7 text-white" />
                </span>
              </div>
              <h1 className="mt-10 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance">
                AI is crafting your resume.
              </h1>
              <div className="mt-8 space-y-3 max-w-sm mx-auto text-left">
                {PROCESSING_STEPS.map((step, i) => (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: i <= stepIdx ? 1 : 0.3 }}
                    className="flex items-center gap-3 text-sm"
                    data-testid={`processing-step-${i}`}
                  >
                    {i < stepIdx ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                    ) : i === stepIdx ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-400 shrink-0" />
                    ) : (
                      <span className="h-4 w-4 rounded-full border border-white/15 shrink-0" />
                    )}
                    <span className={i <= stepIdx ? "text-white" : "text-zinc-500"}>{step}</span>
                  </motion.div>
                ))}
              </div>
              <p className="mt-10 text-xs text-zinc-500">Usually takes under a minute. Don't close this tab.</p>
            </motion.div>
          )}

          {/* ---------- RESULT ---------- */}
          {phase === "result" && job && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="upload-result"
            >
              <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Your AI resume is ready
                </div>
                <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white text-balance" data-testid="result-headline">
                  {job.resume?.full_name ? `${job.resume.full_name}, upgraded.` : "Your resume, upgraded."}
                </h1>
              </div>

              {/* Scores */}
              <div className="mt-10 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-6 text-center" data-testid="score-before">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Before</p>
                  <p className="mt-2 text-4xl font-semibold tracking-tighter text-zinc-400">
                    {job.analysis?.score_before ?? "—"}
                  </p>
                  <p className="mt-1 text-xs text-zinc-600">ATS score</p>
                </div>
                <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-6 text-center" data-testid="score-after">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/80">After</p>
                  <p className="mt-2 text-4xl font-semibold tracking-tighter text-emerald-300">
                    {job.analysis?.score_after ?? "—"}
                  </p>
                  <p className="mt-1 text-xs text-emerald-500/70 flex items-center justify-center gap-1">
                    <TrendingUp className="h-3 w-3" /> ATS score
                  </p>
                </div>
              </div>

              {/* Job match (when a job posting was provided) */}
              {job.analysis?.match_before != null && job.analysis?.match_after != null && (
                <div className="mt-4 grid grid-cols-2 gap-4" data-testid="match-scores">
                  <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5 text-center">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Job match before</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tighter text-zinc-400" data-testid="match-before">
                      {job.analysis.match_before}%
                    </p>
                  </div>
                  <div className="rounded-2xl border border-blue-500/25 bg-blue-500/5 p-5 text-center">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-blue-400/80">Job match after</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tighter text-blue-300" data-testid="match-after">
                      {job.analysis.match_after}%
                    </p>
                  </div>
                </div>
              )}

              {job.analysis?.summary && (
                <p className="mt-6 text-sm text-zinc-400 leading-relaxed text-center text-balance" data-testid="analysis-summary">
                  {job.analysis.summary}
                </p>
              )}

              {/* Downloads */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => handleDownload("pdf")}
                  disabled={downloading !== null}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                  data-testid="download-pdf"
                >
                  {downloading === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download PDF
                </button>
                <button
                  onClick={() => handleDownload("docx")}
                  disabled={downloading !== null}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                  data-testid="download-docx"
                >
                  {downloading === "docx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download DOCX (editable)
                </button>
              </div>

              {/* Analysis details */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {job.analysis?.issues?.length > 0 && (
                  <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-6" data-testid="analysis-issues">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5">
                      <XCircle className="h-3.5 w-3.5 text-red-400/80" /> Fixed issues
                    </p>
                    <ul className="mt-4 space-y-2.5">
                      {job.analysis.issues.map((issue, i) => (
                        <li key={i} className="text-xs text-zinc-400 leading-relaxed flex gap-2">
                          <span className="text-red-400/70 mt-0.5">✕</span>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {job.analysis?.improvements?.length > 0 && (
                  <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-6" data-testid="analysis-improvements">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400/80" /> Improvements made
                    </p>
                    <ul className="mt-4 space-y-2.5">
                      {job.analysis.improvements.map((imp, i) => (
                        <li key={i} className="text-xs text-zinc-300 leading-relaxed flex gap-2">
                          <span className="text-emerald-400/70 mt-0.5">✓</span>
                          {imp}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {job.analysis?.keywords_added?.length > 0 && (
                <div className="mt-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-6" data-testid="analysis-keywords">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">ATS keywords added</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {job.analysis.keywords_added.map((k) => (
                      <span key={k} className="rounded-full border border-blue-500/25 bg-blue-500/10 px-3 py-1 text-xs text-blue-300">
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Resume preview */}
              <div className="mt-10 rounded-3xl border border-white/10 bg-white text-zinc-900 p-8 sm:p-10" data-testid="resume-preview">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-6">Preview</p>
                <h2 className="text-2xl font-bold tracking-tight">{job.resume?.full_name}</h2>
                {job.resume?.headline && <p className="mt-1 text-sm text-zinc-500">{job.resume.headline}</p>}
                <p className="mt-2 text-xs text-zinc-400">
                  {[job.resume?.email, job.resume?.phone, job.resume?.location, job.resume?.linkedin]
                    .filter(Boolean)
                    .join("  ·  ")}
                </p>
                {job.resume?.summary && (
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-wider border-b border-zinc-200 pb-1">Professional Summary</p>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-700">{job.resume.summary}</p>
                  </div>
                )}
                {job.resume?.experience?.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-wider border-b border-zinc-200 pb-1">Experience</p>
                    {job.resume.experience.map((exp, i) => (
                      <div key={i} className="mt-4">
                        <p className="text-sm font-semibold">
                          {[exp.title, exp.company].filter(Boolean).join(" · ")}
                        </p>
                        <p className="text-xs text-zinc-500 italic">
                          {[exp.location, [exp.start_date, exp.end_date].filter(Boolean).join(" — ")]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                        <ul className="mt-2 space-y-1.5">
                          {(exp.bullets || []).map((b, j) => (
                            <li key={j} className="text-sm text-zinc-700 leading-relaxed flex gap-2">
                              <span className="mt-[7px] h-1 w-1 rounded-full bg-zinc-400 shrink-0" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
                {job.resume?.education?.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-wider border-b border-zinc-200 pb-1">Education</p>
                    {job.resume.education.map((edu, i) => (
                      <div key={i} className="mt-3">
                        <p className="text-sm font-semibold">{[edu.degree, edu.institution].filter(Boolean).join(" · ")}</p>
                        <p className="text-xs text-zinc-500">
                          {[edu.location, [edu.start_date, edu.end_date].filter(Boolean).join(" — "), edu.details]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {job.resume?.skills?.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-wider border-b border-zinc-200 pb-1">Skills</p>
                    <p className="mt-3 text-sm text-zinc-700">{job.resume.skills.join("  ·  ")}</p>
                  </div>
                )}
              </div>

              {/* Cover letter (PRO / ELITE) */}
              {job.cover_letter && (
                <div className="mt-8" data-testid="cover-letter-section">
                  <div className="flex items-center gap-2 justify-center mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-500/25 bg-purple-500/10 px-3 py-1 text-xs text-purple-300">
                      <Sparkles className="h-3 w-3" />
                      Included in your plan
                    </span>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-white text-zinc-900 p-8 sm:p-10" data-testid="cover-letter-preview">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-6">Cover letter</p>
                    {(job.cover_letter || "").split("\n").filter((p) => p.trim()).map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-zinc-700 mb-4 last:mb-0">
                        {para}
                      </p>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => handleDownload("cover_pdf")}
                      disabled={downloading !== null}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                      data-testid="download-cover-pdf"
                    >
                      {downloading === "cover_pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Cover letter PDF
                    </button>
                    <button
                      onClick={() => handleDownload("cover_docx")}
                      disabled={downloading !== null}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                      data-testid="download-cover-docx"
                    >
                      {downloading === "cover_docx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Cover letter DOCX
                    </button>
                  </div>
                </div>
              )}

              {/* LinkedIn makeover (ELITE) */}
              {job.linkedin_about && (
                <div className="mt-8" data-testid="linkedin-section">
                  <div className="flex items-center gap-2 justify-center mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/25 bg-sky-500/10 px-3 py-1 text-xs text-sky-300">
                      <Linkedin className="h-3 w-3" />
                      ELITE bonus — LinkedIn About section
                    </span>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-zinc-950/60 p-6 sm:p-8" data-testid="linkedin-preview">
                    {(job.linkedin_about || "").split("\n").filter((p) => p.trim()).map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-zinc-300 mb-4 last:mb-0">
                        {para}
                      </p>
                    ))}
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(job.linkedin_about);
                          setCopied(true);
                          toast.success("Copied — paste it into your LinkedIn About section.");
                          setTimeout(() => setCopied(false), 2500);
                        } catch (_) {
                          toast.error("Couldn't copy", { description: "Select the text and copy manually." });
                        }
                      }}
                      className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
                      data-testid="linkedin-copy"
                    >
                      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? "Copied" : "Copy to clipboard"}
                    </button>
                  </div>
                </div>
              )}

              {/* ELITE multi-job pack */}
              {job.plan === "cv_elite" && (
                <div className="mt-8 rounded-2xl border border-white/10 bg-zinc-950/60 p-6" data-testid="variants-section">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-white font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-300" />
                      Multi-job pack
                    </p>
                    <p className="text-xs text-zinc-500" data-testid="variants-count">
                      {(job.variants || []).length}/2 extra job targets used
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                    Applying to several jobs? Paste another job ad and the AI builds a version targeted to it — same career, different aim.
                  </p>

                  {(job.variants || []).length > 0 && (
                    <div className="mt-4 space-y-3">
                      {job.variants.map((v, vi) => (
                        <div key={v.id} className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid={`variant-${vi}`}>
                          <p className="text-xs text-zinc-400 line-clamp-2">{v.job_posting.slice(0, 140)}…</p>
                          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                            {v.status === "completed" ? (
                              <>
                                {v.analysis?.match_after != null && (
                                  <span className="text-[11px] text-blue-300">Match {v.analysis.match_after}%</span>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleVariantDownload(v.id, "pdf")}
                                    disabled={downloading !== null}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                                    data-testid={`variant-download-pdf-${vi}`}
                                  >
                                    {downloading === `${v.id}:pdf` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                                    PDF
                                  </button>
                                  <button
                                    onClick={() => handleVariantDownload(v.id, "docx")}
                                    disabled={downloading !== null}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                                    data-testid={`variant-download-docx-${vi}`}
                                  >
                                    DOCX
                                  </button>
                                  {v.cover_letter && (
                                    <button
                                      onClick={() => handleVariantDownload(v.id, "cover_pdf")}
                                      disabled={downloading !== null}
                                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                                      data-testid={`variant-download-cover-${vi}`}
                                    >
                                      Cover letter
                                    </button>
                                  )}
                                </div>
                              </>
                            ) : v.status === "processing" ? (
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-blue-300">
                                <Loader2 className="h-3 w-3 animate-spin" /> AI is targeting this job…
                              </span>
                            ) : (
                              <span className="text-[11px] text-red-300">Failed — try again</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(job.variants || []).length < 2 && (
                    <div className="mt-4">
                      <Textarea
                        value={variantPosting}
                        onChange={(e) => setVariantPosting(e.target.value)}
                        rows={3}
                        placeholder="Paste another job posting here…"
                        className="bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                        data-testid="variant-input"
                      />
                      <button
                        onClick={handleAddVariant}
                        disabled={variantBusy || variantPosting.trim().length < 30}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
                        data-testid="variant-add"
                      >
                        {variantBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                        Generate targeted version
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Regenerate */}
              <div className="mt-8 rounded-2xl border border-white/10 bg-zinc-950/60 p-6" data-testid="regenerate-box">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-white font-medium">Not quite right?</p>
                  <p className="text-xs text-zinc-500" data-testid="revisions-remaining">
                    {Math.max(0, (job.max_generations ?? 4) - (job.generations_used ?? 1))} revisions left
                  </p>
                </div>
                {(job.generations_used ?? 1) < (job.max_generations ?? 4) ? (
                  <>
                    <Textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      rows={2}
                      placeholder="Optional: tell the AI what to change (e.g. 'make it more executive', 'shorter summary')…"
                      className="mt-4 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                      data-testid="regenerate-feedback"
                    />
                    <button
                      onClick={handleRegenerate}
                      className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
                      data-testid="regenerate-button"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Regenerate with AI
                    </button>
                  </>
                ) : (
                  <p className="mt-3 text-xs text-zinc-500">
                    Revision limit reached for this order. Need more?{" "}
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zinc-300 underline underline-offset-2">
                      Contact us
                    </a>
                    .
                  </p>
                )}
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/account"
                  className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid="result-back-account"
                >
                  <ArrowRight className="h-4 w-4" />
                  Saved in your account — view anytime
                </Link>
              </div>
            </motion.div>
          )}

          {/* ---------- FORM ---------- */}
          {phase === "form" && (
            <motion.div key="form" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300" data-testid="credit-badge">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Payment confirmed — AI credit ready
                </div>
                <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white text-balance" data-testid="upload-headline">
                  Tell us about your career.
                </h1>
                <p className="mt-4 max-w-xl mx-auto text-zinc-400 text-balance">
                  Upload your current CV and the AI will analyze it, fix its weaknesses,
                  and hand you a recruiter-ready version — in minutes.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="relative mt-12 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-10 space-y-7"
                data-testid="upload-form"
              >
                <fieldset className="space-y-4">
                  <legend className="text-xs uppercase tracking-[0.18em] text-zinc-500">Personal information</legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="full-name" className="text-xs text-zinc-400">Full name *</Label>
                      <Input
                        id="full-name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Jane Doe"
                        required
                        className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                        data-testid="upload-input-fullname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-xs text-zinc-400">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        required
                        className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                        data-testid="upload-input-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="linkedin" className="text-xs text-zinc-400">LinkedIn profile</Label>
                      <Input
                        id="linkedin"
                        value={linkedin}
                        onChange={(e) => setLinkedin(e.target.value)}
                        placeholder="https://linkedin.com/in/janedoe"
                        className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                        data-testid="upload-input-linkedin"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role" className="text-xs text-zinc-400">Desired job role</Label>
                      <Input
                        id="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        placeholder="Senior Product Designer"
                        className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                        data-testid="upload-input-role"
                      />
                    </div>
                  </div>
                </fieldset>

                <div aria-hidden className="h-px w-full bg-white/5" />

                <fieldset className="space-y-3">
                  <legend className="text-xs uppercase tracking-[0.18em] text-zinc-500">Resume language</legend>
                  <div className="flex gap-2" data-testid="language-picker">
                    {[
                      { code: "en", label: "English" },
                      { code: "pt", label: "Português" },
                      { code: "es", label: "Español" },
                    ].map((l) => (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => setLanguage(l.code)}
                        className={`flex-1 rounded-full px-4 py-2.5 text-sm font-medium transition-colors border ${
                          language === l.code
                            ? "bg-white text-black border-white"
                            : "bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10 hover:text-white"
                        }`}
                        data-testid={`language-${l.code}`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div aria-hidden className="h-px w-full bg-white/5" />

                <fieldset className="space-y-4">
                  <legend className="text-xs uppercase tracking-[0.18em] text-zinc-500">Your current CV</legend>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      handleFiles(e.dataTransfer.files);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`cursor-pointer rounded-2xl border border-dashed p-6 text-center transition-colors ${
                      dragOver ? "border-white/40 bg-white/5" : "border-white/15 bg-black/20 hover:bg-white/5"
                    }`}
                    data-testid="upload-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                      data-testid="upload-input-file"
                    />
                    {file ? (
                      <div className="flex items-center justify-between gap-3 text-left">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-5 w-5 text-zinc-300 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">{file.name}</p>
                            <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                          }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-white"
                          data-testid="upload-remove-file"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <UploadCloud className="h-7 w-7 text-zinc-400" />
                        <p className="mt-3 text-sm text-zinc-300">
                          Drag & drop your CV here or <span className="underline underline-offset-2">browse</span>
                        </p>
                        <p className="mt-1 text-[11px] text-zinc-500">PDF, DOCX, TXT · max 10MB</p>
                      </div>
                    )}
                  </div>
                </fieldset>

                <div aria-hidden className="h-px w-full bg-white/5" />

                <fieldset className="space-y-4">
                  <legend className="text-xs uppercase tracking-[0.18em] text-zinc-500">Career details</legend>
                  <div>
                    <Label htmlFor="job-posting" className="text-xs text-zinc-400">
                      Job posting you're applying to <span className="text-emerald-400/90">(recommended)</span>
                    </Label>
                    <Textarea
                      id="job-posting"
                      value={jobPosting}
                      onChange={(e) => setJobPosting(e.target.value)}
                      rows={4}
                      placeholder="Paste the full job ad here — the AI will mirror its exact keywords and requirements so your resume passes that company's ATS. Optional, but this is what makes the resume job-specific."
                      className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                      data-testid="upload-input-job-posting"
                    />
                  </div>
                  <div>
                    <Label htmlFor="experience" className="text-xs text-zinc-400">Work experience</Label>
                    <Textarea
                      id="experience"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      rows={5}
                      placeholder={`Most recent role first. Example:\n\nProduct Designer · Acme Inc · 2022—Present\n- Led redesign of onboarding, +28% activation`}
                      className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                      data-testid="upload-input-experience"
                    />
                  </div>
                  <div>
                    <Label htmlFor="skills" className="text-xs text-zinc-400">Skills</Label>
                    <Textarea
                      id="skills"
                      value={skills}
                      onChange={(e) => setSkills(e.target.value)}
                      rows={3}
                      placeholder="Figma, Design systems, User research, Prototyping, Leadership…"
                      className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                      data-testid="upload-input-skills"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes" className="text-xs text-zinc-400">Additional notes</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Target industries, tone of voice, anything we should highlight…"
                      className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                      data-testid="upload-input-notes"
                    />
                  </div>
                </fieldset>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                    data-testid="upload-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Starting AI…
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-4 w-4" />
                        Generate My AI Resume
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-center text-[11px] text-zinc-500">
                    The AI never invents experience — it only rewrites what you give it.
                  </p>
                </div>
              </form>

              <p className="mt-10 text-center text-xs text-zinc-500">
                Questions? Email us at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zinc-300 hover:text-white underline underline-offset-2" data-testid="upload-support-email">
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
